import apiClient from './apiClient';
import {
  DEFAULT_REVIEW_CONFIG,
  REVIEW_DECISION_STATUS,
  REVIEW_ERROR,
  REVIEW_NOTE_MAX,
  UNSET_KEY,
  decisionStatus,
  isPendingCreator as isPendingWith
} from '../constants/creatorReview';

/*
 * Creators waiting for approval, against Backend 1's API v1.1 (PR #48):
 *   GET  /creators/pending-review?limit=1..100&cursor=  newest first, cursor paging
 *   GET  /creators/pending-review/count                 counts plus the pending definition (getReviewConfig)
 *   GET  /creator-selections/creators                   review=pending|all, sort=newest|oldest
 *   POST /creators/:id/review {decision, note?}         returns previousStatus for Undo
 * Only a bare route 404 means "endpoint not available"; any other error (including a 404 with
 * code 'creator_not_found') is surfaced, with no fallback.
 * Without #48 the service falls back to endpoints that exist on main:
 *   pending list and count -> GET /creators?status=<one value> per pending status, plus missing status
 *   review                 -> PUT /creators/:id/status (the note is not saved)
 * Fallback requests only send plain string or number params, never arrays.
 * Endpoint support and the review config are remembered for the session.
 */

export const SELECTION_PAGE_SIZE = 25;
export const PENDING_LIMIT_MAX = 100;
const FALLBACK_STATUS_LIMIT = 1000;
const MISSING_SCAN_LIMIT = 100;
const MAX_FALLBACK_PAGES = 10;
const CACHE_MS = 30000;

// pendingReview covers both GET routes: #48 adds them together.
const support = { pendingReview: undefined, review: undefined, selectionSort: undefined };
let reviewConfig = null;
let configInFlight = null;
let countInFlight = null;
let fallbackCache = null;
let fallbackInFlight = null;
let countCache = null;
const listeners = new Set();

export const getPendingCreatorsSupport = () => ({ ...support });

/** Forget detected support, the review config and cached results (tests, or after a deploy). */
export function resetPendingCreatorsService() {
  Object.keys(support).forEach(key => { support[key] = undefined; });
  reviewConfig = null;
  configInFlight = null;
  countInFlight = null;
  fallbackCache = null;
  fallbackInFlight = null;
  countCache = null;
}

/** Subscribe to approve, reject and undo changes. Returns the unsubscribe function. */
export function onPendingCreatorsChanged(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function changed() {
  fallbackCache = null;
  fallbackInFlight = null;
  countCache = null;
  listeners.forEach(listener => { try { listener(); } catch (error) { /* a listener never breaks a review */ } });
}

const text = value => (typeof value === 'string' ? value.trim() : '');
const isObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isAbort = error => error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
const statusCode = error => error?.response?.status;
const responseData = error => (isObject(error?.response?.data) ? error.response.data : {});

// A 404 for an unknown or archived creator is a real answer, not a missing route. v1.3 tags it with
// code 'creator_not_found'; the message text is only checked for older servers that send no code.
const isCreatorNotFound = error => {
  if (statusCode(error) !== 404) return false;
  const data = responseData(error);
  if (data.code === REVIEW_ERROR.notFound || data.error === REVIEW_ERROR.notFound) return true;
  return !data.code && /creator not found/i.test(text(data.message));
};

// A bare route 404 ({ error: 'Not Found' }, no code) means the endpoint is not available.
const endpointMissing = error => statusCode(error) === 404 && !isCreatorNotFound(error);

/** The resolved config if it is known, else the constants. Never makes a request. */
export const currentReviewConfig = () => reviewConfig || DEFAULT_REVIEW_CONFIG;

/** isPendingCreator with the server's definition once it is known. */
export const isPendingCreator = creator => isPendingWith(creator, currentReviewConfig());

/** The pending definition: the server's fields win, the constants fill anything missing. */
export function reviewConfigFrom(data) {
  const body = isObject(data) ? data : {};
  const statuses = Array.isArray(body.pendingStatuses) ? body.pendingStatuses.map(text).filter(Boolean) : [];
  const config = {
    pendingStatuses: Object.freeze(statuses.length ? statuses : [...DEFAULT_REVIEW_CONFIG.pendingStatuses]),
    includesMissing: typeof body.includesMissing === 'boolean' ? body.includesMissing : DEFAULT_REVIEW_CONFIG.includesMissing,
    approveStatus: text(body.approveStatus) || DEFAULT_REVIEW_CONFIG.approveStatus,
    rejectStatus: text(body.rejectStatus) || DEFAULT_REVIEW_CONFIG.rejectStatus
  };
  config.fromServer = Boolean(statuses.length || typeof body.includesMissing === 'boolean' || text(body.approveStatus) || text(body.rejectStatus));
  return Object.freeze(config);
}

const count = value => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : null;
};

const countMap = value => (isObject(value)
  ? Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, count(entry)]).filter(([, entry]) => entry != null))
  : null);

function normalizeCounts(data) {
  const body = isObject(data) ? data : {};
  const byStatus = countMap(body.byStatus) || {};
  const total = count(body.count) ?? Object.values(byStatus).reduce((sum, entry) => sum + entry, 0);
  return { count: total, byStatus, bySource: countMap(body.bySource), last7: count(body.last7), last30: count(body.last30), last90: count(body.last90) };
}

// One shared count request (config, page and nav badge). Resolves to the body, or null on a 404.
function requestCount() {
  if (support.pendingReview === false) return Promise.resolve(null);
  if (!countInFlight) {
    const run = apiClient.get('/creators/pending-review/count').then(({ data }) => {
      support.pendingReview = true;
      const body = isObject(data) ? data : {};
      if (!reviewConfig) reviewConfig = reviewConfigFrom(body);
      countCache = { at: Date.now(), counts: normalizeCounts(body) };
      return body;
    }, error => {
      if (!endpointMissing(error)) throw error;
      support.pendingReview = false;
      return null;
    }).finally(() => { if (countInFlight === run) countInFlight = null; });
    countInFlight = run;
  }
  return countInFlight;
}

/**
 * The pending definition and decision statuses, memoised for the session:
 * `{ pendingStatuses, includesMissing, approveStatus, rejectStatus, fromServer }`.
 * Read from GET /creators/pending-review/count; the constants only on a 404. Other errors are thrown.
 */
export function getReviewConfig() {
  if (reviewConfig) return Promise.resolve(reviewConfig);
  if (!configInFlight) {
    const run = requestCount().then(() => {
      if (!reviewConfig) reviewConfig = reviewConfigFrom(null);
      return reviewConfig;
    }).finally(() => { if (configInFlight === run) configInFlight = null; });
    configInFlight = run;
  }
  return configInFlight;
}

const idTime = id => (/^[a-f\d]{24}$/i.test(id) ? parseInt(id.slice(0, 8), 16) * 1000 : null);

const dateOf = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * One creator shape for every source: pending-review rows, GET /creators rows and creator
 * selection rows ({ creatorId, creatorStatus, creatorSource, creatorCreatedAt, source: snapshot }).
 * Returns null for anything unusable.
 */
export function normalizeCreator(raw) {
  if (!isObject(raw)) return null;
  const snapshot = isObject(raw.source) ? raw.source : null;
  const id = text(String(raw._id || raw.creatorId || raw.id || ''));
  if (!id) return null;
  const twitter = isObject(raw.twitter) ? raw.twitter : {};
  const handle = (text(snapshot?.handle) || text(twitter.username) || text(raw.handle) || text(raw.twitterHandle)).replace(/^@/, '');
  const status = text(raw.creatorStatus) || text(raw.status) || text(snapshot?.status) || null;
  const source = text(raw.creatorSource) || text(typeof raw.source === 'string' ? raw.source : '') || text(snapshot?.creatorSource) || null;
  const createdAt = dateOf(raw.creatorCreatedAt) || dateOf(raw.createdAt) || dateOf(snapshot?.createdAt);
  return {
    _id: id,
    name: text(raw.name) || text(snapshot?.name) || handle || 'Unnamed creator',
    handle,
    followers: count(snapshot?.followers ?? twitter.followers ?? twitter.followersCount ?? raw.followers),
    status,
    source,
    createdAt,
    createdTime: createdAt ? new Date(createdAt).getTime() : idTime(id),
    pfp: text(twitter.profileImageUrl) || text(raw.pfp) || null
  };
}

function rowsOf(data) {
  if (Array.isArray(data)) return data;
  if (!isObject(data)) return [];
  for (const key of ['creators', 'items', 'data']) {
    if (Array.isArray(data[key])) return data[key];
    if (isObject(data[key])) return rowsOf(data[key]);
  }
  return [];
}

const toCreators = data => rowsOf(data).map(normalizeCreator).filter(Boolean);

function dedupe(creators) {
  const seen = new Set();
  return creators.filter(creator => !seen.has(creator._id) && seen.add(creator._id));
}

/** Newest first by createdAt, else by the id timestamp; unknown dates last. */
export const sortNewest = creators => [...creators].sort((a, b) => (b.createdTime ?? -Infinity) - (a.createdTime ?? -Infinity));

async function statusPages(status) {
  const creators = [];
  let cursor;
  for (let page = 0; page < MAX_FALLBACK_PAGES; page += 1) {
    const { data } = await apiClient.get('/creators', {
      params: { status, limit: FALLBACK_STATUS_LIMIT, sort: 'createdAt', order: 'desc', ...(cursor ? { cursor } : {}) }
    });
    creators.push(...toCreators(data));
    const next = text(typeof data?.pagination?.nextCursor === 'string' ? data.pagination.nextCursor : '');
    if (!data?.pagination?.hasMore || !next || next === cursor) break;
    cursor = next;
  }
  return creators;
}

// Sorting by status ascending puts creators without a status first, so a short scan finds them all.
async function missingStatusPages() {
  const creators = [];
  for (let page = 1; page <= MAX_FALLBACK_PAGES; page += 1) {
    const { data } = await apiClient.get('/creators', { params: { sort: 'status', order: 'asc', limit: MISSING_SCAN_LIMIT, page } });
    const rows = toCreators(data);
    const missing = rows.filter(creator => !creator.status);
    creators.push(...missing);
    if (missing.length < rows.length || rows.length < MISSING_SCAN_LIMIT) break;
  }
  return creators;
}

// One shared computation for every caller (page, nav badge, picker), so it is not cancelled by any one
// of them: a caller that unmounts simply ignores the result.
function fallbackPending(config) {
  if (fallbackCache && Date.now() - fallbackCache.at < CACHE_MS) return Promise.resolve(fallbackCache.creators);
  if (!fallbackInFlight) {
    const run = Promise.all([
      ...config.pendingStatuses.map(status => statusPages(status)),
      config.includesMissing ? missingStatusPages() : Promise.resolve([])
    ]).then(lists => {
      const creators = sortNewest(dedupe(lists.flat().filter(creator => isPendingWith(creator, config))));
      if (fallbackInFlight === run) fallbackCache = { at: Date.now(), creators };
      return creators;
    }).finally(() => { if (fallbackInFlight === run) fallbackInFlight = null; });
    fallbackInFlight = run;
  }
  return fallbackInFlight;
}

const pageLimit = limit => Math.min(PENDING_LIMIT_MAX, Math.max(1, parseInt(limit, 10) || 50));

/**
 * One page of pending creators, newest first: `{ creators, nextCursor, hasMore, mode }`.
 * Pass the returned nextCursor to get the next page. mode is 'server' (pending-review, in the
 * server's order) or 'fallback' (the cursor is an offset into the computed list).
 */
export async function listPending({ limit = 50, cursor, signal } = {}) {
  const config = await getReviewConfig();
  const size = pageLimit(limit);
  if (support.pendingReview !== false) {
    try {
      const { data } = await apiClient.get('/creators/pending-review', { params: { limit: size, ...(cursor ? { cursor: String(cursor) } : {}) }, signal });
      support.pendingReview = true;
      const pagination = isObject(data?.pagination) ? data.pagination : {};
      const nextCursor = text(typeof pagination.nextCursor === 'string' ? pagination.nextCursor : '') || null;
      return { creators: dedupe(toCreators(data)), nextCursor, hasMore: Boolean(pagination.hasMore && nextCursor), mode: 'server' };
    } catch (error) {
      if (isAbort(error) || !endpointMissing(error)) throw error;
      support.pendingReview = false;
    }
  }
  const all = await fallbackPending(config);
  const offset = Math.max(0, parseInt(cursor, 10) || 0);
  const end = offset + size;
  return { creators: all.slice(offset, end), nextCursor: end < all.length ? String(end) : null, hasMore: end < all.length, mode: 'fallback' };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `{ count, byStatus, bySource, last7, last30, last90 }` from a list, with a missing status under
 * `unset` like the server. bySource is null when no source is known.
 */
export function computeCounts(creators, now = Date.now()) {
  const byStatus = {};
  const bySource = {};
  let sourceKnown = false;
  const since = days => creators.filter(creator => creator.createdTime != null && now - creator.createdTime <= days * DAY_MS).length;
  creators.forEach(creator => {
    const status = creator.status || UNSET_KEY;
    byStatus[status] = (byStatus[status] || 0) + 1;
    if (creator.source) {
      sourceKnown = true;
      bySource[creator.source] = (bySource[creator.source] || 0) + 1;
    }
  });
  return { count: creators.length, byStatus, bySource: sourceKnown ? bySource : null, last7: since(7), last30: since(30), last90: since(90) };
}

/** Pending counts for the nav badge and status chips. */
export async function countPending() {
  if (countCache && Date.now() - countCache.at < CACHE_MS) return countCache.counts;
  const body = await requestCount();
  if (body) return normalizeCounts(body);
  const counts = computeCounts(await fallbackPending(await getReviewConfig()));
  countCache = { at: Date.now(), counts };
  return counts;
}

const matchesSearch = (creator, search) => {
  const query = text(search).toLowerCase();
  return !query || creator.name.toLowerCase().includes(query) || creator.handle.toLowerCase().includes(query);
};

/** A pending creator in the creator selection row shape, for display until the real row is fetched on add. */
const pendingRow = (creator, basis) => ({
  creatorId: creator._id,
  include: true,
  overrides: {},
  creatorStatus: creator.status,
  creatorSource: creator.source,
  creatorCreatedAt: creator.createdAt,
  fallback: true,
  source: { name: creator.name, handle: creator.handle, notes: '', tagIds: [], followers: creator.followers, rate: null, currency: null, basis }
});

// #48 sends top level review fields and applies sort; without them, an ascending page of ObjectIds
// means the server ignored sort and kept its oldest first order.
const hasReviewFields = rows => rows.some(row => isObject(row) && ('creatorCreatedAt' in row || 'creatorStatus' in row));
const ascendingIds = rows => rows.length > 1 && rows.every((row, index) => index === 0 || String(rows[index - 1].creatorId) < String(row.creatorId));

// Page only fallback: by createdAt, else the id time (servers that ignore sort send no createdAt).
const sortRowsNewest = rows => rows
  .map(row => ({ row, time: normalizeCreator(row)?.createdTime ?? -Infinity }))
  .sort((a, b) => b.time - a.time)
  .map(entry => entry.row);

// #48 adds creatorStatus to every row; older servers put no status on selection rows.
const hasStatusField = row => 'creatorStatus' in row || 'status' in row || 'status' in row.source;

/**
 * Creator selection picker rows. Sends review=pending and sort=newest only when asked for, so with
 * both options off the request and the server's default order (_id ascending) are unchanged.
 * Servers without #48 ignore both.
 * Returns `{ creators, hasMore, mode, sortScope }`:
 *   mode 'server' (as returned), 'filtered' (statuses known, filtered here) or 'fallback' (pending list);
 *   sortScope 'all' when newest first covers every page, 'page' when it only orders the current page.
 */
export async function listSelectionCandidates({ basis, search = '', page = 1, selectionId, tagIds = '', review = 'all', sort = 'oldest', signal } = {}) {
  const params = {
    basis, search, page,
    ...(selectionId ? { selectionId } : {}),
    ...(tagIds ? { tagIds } : {}),
    ...(review === 'pending' ? { review: 'pending' } : {}),
    ...(sort === 'newest' ? { sort: 'newest' } : {})
  };
  const { data } = await apiClient.get('/creator-selections/creators', { params, signal });
  let rows = (Array.isArray(data?.creators) ? data.creators : []).filter(row => isObject(row) && row.creatorId && isObject(row.source));
  const hasMore = Boolean(data?.hasMore);
  let mode = 'server';

  if (params.review === 'pending') {
    if (!rows.every(hasStatusField)) {
      const all = (await fallbackPending(await getReviewConfig())).filter(creator => matchesSearch(creator, search));
      const start = (Math.max(1, page) - 1) * SELECTION_PAGE_SIZE;
      return {
        creators: all.slice(start, start + SELECTION_PAGE_SIZE).map(creator => pendingRow(creator, basis)),
        hasMore: start + SELECTION_PAGE_SIZE < all.length,
        mode: 'fallback',
        sortScope: 'all'
      };
    }
    const pending = rows.filter(row => isPendingCreator(normalizeCreator(row)));
    if (pending.length !== rows.length) mode = 'filtered';
    rows = pending;
  }

  let sortScope = 'all';
  if (params.sort === 'newest') {
    if (support.selectionSort === undefined && hasReviewFields(data?.creators || [])) support.selectionSort = true;
    else if (support.selectionSort === undefined && ascendingIds(data?.creators || [])) support.selectionSort = false;
    else if (support.selectionSort === undefined && rows.length > 1) support.selectionSort = true;
    // A server that applies sort (v1.3: createdAt, then _id) is kept in its order; only a page it left
    // in _id order is re-sorted here.
    if (support.selectionSort === false) {
      sortScope = 'page';
      rows = sortRowsNewest(rows);
    }
  }
  return { creators: rows, hasMore, mode, sortScope };
}

/**
 * The real selection row for a pending creator shown from the fallback list, or null when the
 * selection excludes it (already shared with the brand, excluded handle, tag filters).
 */
export async function resolveSelectionRow(row, { basis, selectionId, tagIds = '', signal } = {}) {
  if (!row?.fallback) return row;
  const handle = text(row.source?.handle);
  const name = text(row.source?.name);
  for (const search of [handle, name].filter(Boolean)) {
    const { data } = await apiClient.get('/creator-selections/creators', {
      params: { basis, search, page: 1, ...(selectionId ? { selectionId } : {}), ...(tagIds ? { tagIds } : {}) },
      signal
    });
    const match = (Array.isArray(data?.creators) ? data.creators : []).find(candidate => String(candidate?.creatorId) === String(row.creatorId));
    if (match) return match;
  }
  return null;
}

/** True when a review note would not be saved (the review endpoint is missing, or very likely missing). */
export const reviewNotesUnavailable = () => support.review === false || (support.review === undefined && support.pendingReview === false);

const tagError = (error, reviewError, extra = {}) => Object.assign(error, { reviewError }, extra);

// Every /creators/:id route on #48 answers 400 { error: 'invalid_id' } for a malformed id.
const isInvalidId = error => statusCode(error) === 400 && responseData(error).error === 'invalid_id';

// PUT /creators/:id/status answers 400 { error: 'invalid_status', allowed } for a value outside the enum.
async function putStatus(creatorId, status) {
  try {
    await apiClient.put(`/creators/${encodeURIComponent(String(creatorId))}/status`, { status });
  } catch (error) {
    const data = responseData(error);
    if (statusCode(error) === 400 && data.error === 'invalid_status') {
      throw tagError(error, REVIEW_ERROR.invalidStatus, { status, allowed: Array.isArray(data.allowed) ? data.allowed : [] });
    }
    if (isInvalidId(error)) throw tagError(error, REVIEW_ERROR.invalidId);
    if (isCreatorNotFound(error)) {
      changed();
      throw tagError(error, REVIEW_ERROR.notFound);
    }
    throw error;
  }
}

/**
 * Approve or reject: `{ creatorId, status, previousStatus, noteSaved, mode }`.
 * previousStatus is null when Undo isn't possible. `previousStatus` in options is the status the
 * caller listed, used only by the PUT fallback (the review endpoint returns its own).
 * Errors carry `reviewError`: 'already_reviewed' (409 CREATOR_NOT_PENDING; `currentStatus` is null
 * only when the server sends no status, as before v1.3), 'creator_not_found' (404 for an unknown or
 * archived creator, never followed by the PUT fallback), 'invalid_id' (400) or 'invalid_status'
 * (400 from the PUT fallback). Cached lists and counts are cleared after the first two. None of them
 * is worth retrying.
 */
export async function reviewCreator(creatorId, decision, { note, previousStatus } = {}) {
  if (!Object.prototype.hasOwnProperty.call(REVIEW_DECISION_STATUS, decision)) throw new Error(`Unknown review decision: ${decision}`);
  const config = await getReviewConfig();
  const status = decisionStatus(decision, config);
  const id = encodeURIComponent(String(creatorId));
  const trimmedNote = text(note).slice(0, REVIEW_NOTE_MAX);
  if (support.review !== false) {
    try {
      const { data } = await apiClient.post(`/creators/${id}/review`, { decision, ...(trimmedNote ? { note: trimmedNote } : {}) });
      support.review = true;
      changed();
      return {
        creatorId: String(creatorId),
        status: text(data?.creator?.status) || status,
        previousStatus: text(data?.previousStatus) || null,
        noteSaved: Boolean(trimmedNote),
        mode: 'review'
      };
    } catch (error) {
      if (statusCode(error) === 409 && responseData(error).code === 'CREATOR_NOT_PENDING') {
        changed();
        throw tagError(error, REVIEW_ERROR.alreadyReviewed, { currentStatus: text(responseData(error).status) || null });
      }
      if (isInvalidId(error)) throw tagError(error, REVIEW_ERROR.invalidId);
      if (isCreatorNotFound(error)) {
        changed();
        throw tagError(error, REVIEW_ERROR.notFound);
      }
      if (!endpointMissing(error)) throw error;
      support.review = false;
    }
  }
  await putStatus(creatorId, status);
  changed();
  return { creatorId: String(creatorId), status, previousStatus: text(previousStatus) || null, noteSaved: false, mode: 'status' };
}

/** Undo: put previousStatus back. There is no status to restore when it is empty. */
export async function restoreCreatorStatus(creatorId, previousStatus) {
  const status = text(previousStatus);
  if (!status) throw new Error('There is no previous status to restore.');
  await putStatus(creatorId, status);
  changed();
  return { creatorId: String(creatorId), status };
}

const pendingCreatorsService = {
  getReviewConfig,
  listPending,
  countPending,
  listSelectionCandidates,
  resolveSelectionRow,
  reviewCreator,
  restoreCreatorStatus,
  reviewNotesUnavailable,
  onPendingCreatorsChanged
};

export default pendingCreatorsService;
